import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../../../components/button/button";
import hackingVideo from "../../../assets/videos/course_intro.mp4"; // optional: replace with your video

// Accordion components
const Accordion = ({ children }) => (
  <div className="border border-gray-200 divide-y rounded-2xl overflow-hidden shadow-sm bg-white">
    {children}
  </div>
);

const AccordionItem = ({ title, content }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="p-4 hover:bg-gray-50 transition">
      <h3>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full justify-between items-center text-left text-lg font-semibold text-[#0f172a] focus:outline-none transition"
          aria-expanded={isOpen}
        >
          <span>{title}</span>
          <motion.span
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-gray-600 text-xl"
          >
            ▶
          </motion.span>
        </button>
      </h3>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden mt-3 text-gray-700"
          >
            <ul className="list-disc pl-6 space-y-2">
              {content.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const CourseDescription = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const accordionData = [
    {
      title: "🧠 Module 1: Introduction to Ethical Hacking",
      content: [
        "Understand hacking concepts, terminologies, and principles of ethical hacking",
        "Legal implications and frameworks (laws, compliance, and responsible disclosure)",
        "Ethical hacking phases — Reconnaissance, Scanning, Exploitation, and Reporting",
        "Setting up your hacking lab (VirtualBox, Kali Linux, Metasploitable, OWASP DVWA)",
      ],
    },
    {
      title: "🔍 Module 2: Footprinting and Reconnaissance",
      content: [
        "Gather intelligence using open-source tools (OSINT)",
        "DNS enumeration, WHOIS lookups, and network mapping",
        "Social engineering and phishing awareness",
        "Website reconnaissance and metadata extraction",
      ],
    },
    {
      title: "💻 Module 3: Scanning Networks and Systems",
      content: [
        "Active and passive scanning techniques",
        "Nmap deep dive — host discovery and port scanning",
        "Vulnerability scanning using Nessus and OpenVAS",
        "Firewall evasion, banner grabbing, and service detection",
      ],
    },
    {
      title: "⚔️ Module 4: System Hacking and Privilege Escalation",
      content: [
        "Password cracking and brute force attacks",
        "Privilege escalation on Windows and Linux systems",
        "Maintaining access and clearing logs",
        "Using Metasploit Framework for exploitation",
      ],
    },
    {
      title: "🌐 Module 5: Web Application Hacking",
      content: [
        "Understanding web app architecture and HTTP protocols",
        "SQL Injection, XSS, and Command Injection attacks",
        "File upload vulnerabilities and path traversal",
        "Burp Suite advanced exploitation and automation",
      ],
    },
    {
      title: "🔒 Module 6: Wireless, Cloud & Mobile Hacking",
      content: [
        "Wireless security basics and cracking WEP/WPA2",
        "Man-in-the-middle attacks using Ettercap and Wireshark",
        "Cloud vulnerabilities and container security fundamentals",
        "Android application penetration testing basics",
      ],
    },
    {
      title: "🏁 Module 7: Final Projects & Certification Prep",
      content: [
        "Real-world penetration testing simulations",
        "Reporting and documentation of findings",
        "CEH and OSCP-style exam preparation",
        "Portfolio and career readiness guidance",
      ],
    },
  ];

  return (
    <div className="w-full bg-gradient-to-b from-white to-[#f9fafb] text-[#1a1a1a] font-inter py-10 px-6 sm:px-10 lg:px-20 relative overflow-hidden">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-left mb-10"
      >
        <h1 className="text-4xl font-extrabold tracking-tight text-[#0f172a]">
          Ethical Hacking & Penetration Testing 
        </h1><br />
        <p className="text-lg text-gray-600 mt-2 max-w-3xl">
          Become a certified ethical hacker by mastering the tools and techniques used by real attackers.
          In this hands-on bootcamp, you’ll learn how to identify, exploit, and secure vulnerabilities —
          gaining practical experience through labs, simulations, and real-world projects.
          No prior experience is needed; just curiosity and determination.
        </p>
      </motion.div>

      {/* VIDEO */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-4xl mx-auto mb-12 rounded-2xl overflow-hidden shadow-xl"
      >
        <video src={hackingVideo} controls className="w-full h-[23rem] object-cover" />
      </motion.div>

      {/* DESCRIPTION */}
      <section className="max-w-5xl mx-auto mb-12">
        <h2 className="text-2xl font-bold mb-4 text-[#0f172a]">
          What Does an Ethical Hacker Do?
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          Ethical hackers — also known as penetration testers — simulate cyberattacks to uncover system vulnerabilities before malicious hackers do.
          They play a crucial role in improving digital defenses for organizations, government agencies, and startups worldwide.
          You’ll learn to test networks, exploit vulnerabilities, and secure systems like a pro.
          This course prepares you for certifications like CEH and OSCP, equipping you with the skills to launch a rewarding career in cybersecurity.
        </p>
      </section>

      {/* WHAT YOU’LL LEARN */}
      <section className="max-w-5xl mx-auto mb-16">
        <h2 className="text-2xl font-bold mb-6 text-[#0f172a]">What You’ll Learn</h2>
        <Accordion>
          {accordionData.map((item, index) => (
            <AccordionItem key={index} title={item.title} content={item.content} />
          ))}
        </Accordion>
      </section>

      {/* COURSE COST */}
      <section className="max-w-6xl mx-auto bg-white rounded-3xl shadow-lg border border-gray-100 p-10 mb-16">
        <h2 className="text-3xl font-extrabold mb-10 text-center text-[#0f172a]">
          💼 Course Cost Breakdown
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              title: "Ethical Hacking (Weekdays)",
              price: "₦500,000",
              color: "from-emerald-500 to-blue-600",
              modules: [
                // "Introduction & Footprinting — ₦100,000",
                // "Network Scanning & Exploitation — ₦150,000",
                // "Web & System Hacking — ₦150,000",
                // "Capstone & Certification Prep — ₦100,000",
              ],
            },
            {
              title: "Ethical Hacking (Weekends)",
              price: "₦550,000",
              color: "from-indigo-500 to-purple-600",
              modules: [
                // "Flexible weekend schedule",
                // "Live mentorship & guided labs",
                // "Hands-on red team simulations",
                // "Career and certification support",
              ],
            },
          ].map((course, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6"
            >
              <h3
                className={`text-xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r ${course.color}`}
              >
                {course.title}
              </h3>
              <p className="text-2xl font-extrabold text-[#0f172a] mb-6">{course.price}</p>
              <ul className="space-y-3 text-gray-700 text-sm">
                {course.modules.map((mod, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 hover:bg-gray-100 transition"
                  >
                    <span className="text-[#0f172a] font-medium">•</span>
                    <span>{mod}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      {/* <div className="text-center mt-16">
        <Link to="/get-started">
          <Button
            btnText="Join the Ethical Hacking Bootcamp"
            btnClassName="!text-lg !bg-[#0f172a] hover:!bg-[#1e293b] text-white px-10 py-4 rounded-xl shadow-lg hover:shadow-2xl transition duration-300 ease-in-out"
          />
        </Link>
      </div> */}

              {/* AUTO POPUP */}
      {showPopup && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-8 right-8 bg-[#0f172a] text-white rounded-xl shadow-2xl p-6 w-80 z-50"
        >
          <h3 className="text-lg font-bold mb-2">🎉 Get 20% Off!</h3>
          <p className="text-sm mb-4 text-gray-200">
            Pay within the next 24 hours and get 20% off your course fee.
          </p>
          <Button
            btnText="Claim Discount"
            btnClassName="!bg-white !text-[#0f172a] font-semibold w-full py-2 rounded-lg hover:!bg-gray-100 transition"
            onClick={() => setShowDiscountModal(true)}
          />
        </motion.div>
      )}

      {/* FLOATING DISCOUNT BUTTON */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        className="fixed bottom-8 left-8 bg-[#0f172a] text-white font-semibold px-5 py-3 rounded-full shadow-xl hover:bg-[#1e293b] transition z-50"
        onClick={() => setShowDiscountModal(true)}
      >
        💰 Get 20% Discount
      </motion.button>

      {/* DISCOUNT MODAL */}
 {/* DISCOUNT MODAL */}

<AnimatePresence>
  {
  showDiscountModal && (
    <>
      {/* Background Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black backdrop-blur-sm z-40"
        onClick={() => setShowDiscountModal(false)}
      />

      {/* Modal Box */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="fixed z-50 inset-0 flex items-center justify-center"
      >
        <div className="relative bg-white w-[22rem] sm:w-[26rem] rounded-2xl shadow-2xl p-6 text-center">
          {/* Close Button */}
          <button
            onClick={() => setShowDiscountModal(false)}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-semibold transition"
            aria-label="Close discount modal"
          >
            &times;
          </button>

          <h3 className="text-2xl font-bold text-[#0f172a] mb-2">
            🎁 Claim Your 20% Discount
          </h3>
          <p className="text-gray-600 mb-4 text-sm">
            Fill in your details below to lock in your offer.
          </p>

          <form className="space-y-3">
            <input
              type="text"
              placeholder="Full Name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0f172a] outline-none"
            />
            <input
              type="email"
              placeholder="Email Address"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0f172a] outline-none"
            />
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0f172a] outline-none">
              <option value="">Select Course</option>
              <option value="cybersecurity_beginners">Cybersecurity Beginners </option>
              <option value="cybersecurity_ceh">Certified Ethical Hacking(CEH)</option>
            </select>
            <Button
              btnText="Pay Now & Get 20% Off"
              btnClassName="!bg-[#0f172a] text-white w-full py-2 rounded-lg hover:!bg-[#1e293b] transition"
              //onclic take teh ktk ot thi slink https://paystack.com/buy/software-development-courses-ucgtjo
              onClick={() => {
                window.open("https://paystack.com/buy/cybersecurity-courses-bfupfr", "_blank");
              }}
            />
          </form>
        </div>
      </motion.div>
    </>
  )}
</AnimatePresence>
    </div>
  );
};

export default CourseDescription;
